import { useQuery } from "@tanstack/react-query";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import apiClient from "@/lib/api-client";

export type AngleRescoreState =
  | "queued" | "settling" | "deferred" | "retrying" | "running" | "batch" | "failed";

export interface ActivityResponse {
  angle_rescore: {
    state: AngleRescoreState;
    requested_at: string | null;
    not_before: string | null;
    failures: number;
  } | null;
  running: {
    kind: "field_debate_build" | "field_debate_refresh" | "monitor";
    started_at: string;
    automatic: boolean;
    detail: string | null;
    /** A Field Debate build waiting on the Batch API with no live job. */
    batch?: boolean;
  }[];
  finished: {
    kind: "monitor" | "field_debate_build" | "field_debate_refresh" | "scoring_batch" | "abstract_backfill";
    finished_at: string;
    outcome: "success" | "failed" | "cancelled" | "skipped";
    duration_seconds: number | null;
    automatic: boolean;
    count: number | null;
    failed_count: number | null;
    error: string | null;
  }[];
  sync_sources: {
    provider: string;
    status: string;
    last_checked_at: string | null;
    checking: boolean;
    attached_today: { paper_id: string; title: string; at: string }[];
  }[];
  abstract_backfill_running: boolean;
  elsewhere_running: number;
  /** Slice 1c: this concept's waiting/running runs + those finished today. */
  jobs?: JobRun[];
}

export type JobRunStatus = "waiting" | "running" | "done" | "failed" | "cancelled" | "stopped";

export interface JobRun {
  id: string;
  kind: string;
  status: JobRunStatus;
  done: number | null;
  total: number | null;
  detail: string | null;
  summary: string | null;
  error: string | null;
  automatic: boolean;
  params: Record<string, unknown>;
  progress: { done_seed_ids?: string[] } & Record<string, unknown>;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

/** Start of the viewer's day: "Earlier today" is local, not UTC. */
export function startOfToday(now = new Date()): Date {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Server rows for the Activity panel. The op bar polls ("open": every 15s,
 * "closed": once a minute, so a scheduled run that lands while the panel is
 * closed still raises the unread count). Other readers share its cache and
 * don't add timers of their own ("passive"). */
export function useActivity(conceptId: string | undefined, poll: "open" | "closed" | "passive") {
  // The quick poll serves durable-jobs pills; everyone else keeps 15 s / 60 s.
  const durable = !!useCurrentUser().data?.features?.durable_jobs;
  return useQuery({
    queryKey: ["activity", conceptId],
    queryFn: () =>
      apiClient
        .get(`api/concepts/${conceptId}/activity`, {
          searchParams: { since: startOfToday().toISOString() },
        })
        .json<ActivityResponse>(),
    enabled: !!conceptId,
    // While a job runs or waits, poll often enough for its pill (the
    // server writes progress every 2 s).
    refetchInterval: (query) => {
      if (poll === "passive") return false;
      const active = durable && query.state.data?.jobs?.some((j) => j.status === "running" || j.status === "waiting");
      return active ? 5_000 : poll === "open" ? 15_000 : 60_000;
    },
    staleTime: 10_000,
  });
}
